import { toast } from 'sonner';
import { Smartphone, Apple, Monitor, AppWindow, QrCode, FileText, Laptop, Globe, Download as DownloadIcon } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { Reveal, Stagger, StaggerItem } from '@/components/Reveal';
import { useLanguage } from '@/context/LanguageContext';

function QrMock() {
  const cells = [];
  for (let y = 0; y < 21; y++) {
    for (let x = 0; x < 21; x++) {
      const finder = (x < 5 && y < 5) || (x > 15 && y < 5) || (x < 5 && y > 15);
      const on = finder ? (x === 0 || x === 4 || y === 0 || y === 4 || (x > 1 && x < 4 && y > 1 && y < 4) || (x === 16 && y === 0) || (x === 20 && y === 4)) : (x * 7 + y * 13 + ((x * y) % 5)) % 3 === 0;
      if (on) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#0F172A" rx="0.2" />);
    }
  }
  return (
    <svg viewBox="-1 -1 23 23" className="h-40 w-40" data-testid="qr-mock">
      <rect x="-1" y="-1" width="23" height="23" fill="#fff" rx="1.5" />
      {cells}
    </svg>
  );
}

export default function Download() {
  const { t } = useLanguage();
  const badges = t('download.badges');
  const notes = t('download.notes');
  const reqs = t('download.reqs.rows');

  const links = t('download.links');
  const show = t('download.show');
  const items = [
    { icon: Apple, label: badges.ios, testid: 'download-ios', href: links.ios },
    { icon: Smartphone, label: badges.android, testid: 'download-android', href: links.android },
    { icon: FileText, label: badges.apk, testid: 'download-apk', href: links.apk },
    { icon: Monitor, label: badges.win, testid: 'download-win', href: links.win },
    { icon: Laptop, label: badges.mac, testid: 'download-mac', href: links.mac },
    { icon: Globe, label: badges.web, testid: 'download-web', href: links.web },
  ].filter((it) => show[it.testid.replace('download-', '')] !== false);

  const click = (e, it) => {
    if (!it.href || it.href === '#') { e.preventDefault(); toast.info(it.label, { description: `WiyakGPS · ${notes.version}` }); }
  };

  return (
    <div className="pt-28" data-testid="download-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="download.eyebrow" title="download.title" sub="download.sub" align="center" />

        <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <StaggerItem key={it.testid}>
              <a
                href={it.href || '#'}
                target={it.href && it.href !== '#' ? '_blank' : undefined}
                rel="noreferrer"
                onClick={(e) => click(e, it)}
                data-testid={`${it.testid}-btn`}
                className="card-hover flex w-full items-center gap-4 rounded-2xl border border-brand-line bg-white p-6 text-start"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-blue/8 text-brand-blue border border-brand-blue/15"><it.icon className="h-5 w-5" /></span>
                <span className="font-display text-base font-bold text-brand-ink">{it.label}</span>
              </a>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-4 lg:px-8" data-testid="download-docs">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-5 rounded-[2rem] border border-brand-line bg-white p-8 sm:flex-row sm:items-center" data-testid="download-docs-card">
            <div className="flex items-center gap-4">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-teal/10 text-brand-teal border border-brand-teal/20"><FileText className="h-5 w-5" /></span>
              <div>
                <h3 className="font-display text-lg font-extrabold text-brand-ink">{t('download.docs.title')}</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {t('download.docs.items').map((d) => (
                    <a key={d.k} href={d.href} download data-testid={`download-doc-${d.k}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-4 py-2 text-xs font-bold text-brand-ink transition-all duration-300 hover:border-brand-blue hover:text-brand-blue">
                      <DownloadIcon className="h-3.5 w-3.5" /> {d.k}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl gap-5 px-5 py-16 lg:grid-cols-3 lg:px-8">
        {t('download.showQr') !== false && <Reveal>
          <div className="flex h-full flex-col items-center justify-center rounded-[2rem] border border-brand-line bg-white p-8 text-center" data-testid="download-qr-card">
            <QrCode className="h-5 w-5 text-brand-blue" />
            <h3 className="mt-3 font-display text-lg font-extrabold text-brand-ink">{t('download.qr.title')}</h3>
            <p className="mt-1 text-xs text-brand-steel">{t('download.qr.sub')}</p>
            <div className="mt-6 rounded-2xl border border-brand-line p-3"><QrMock /></div>
          </div>
        </Reveal>}

        {t('download.showNotes') !== false && <Reveal delay={0.1}>
          <div className="h-full rounded-[2rem] border border-brand-line bg-white p-8" data-testid="download-notes">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-extrabold text-brand-ink">{notes.title}</h3>
              <span className="rounded-full bg-brand-blue/8 border border-brand-blue/15 px-3 py-1 font-mono text-[10px] font-bold text-brand-blue" dir="ltr">{notes.version}</span>
            </div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-brand-dim">{notes.date}</p>
            <ul className="mt-5 space-y-3">
              {notes.items.filter(Boolean).map((n, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm font-semibold text-brand-steel">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-blue" /> {n}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>}

        {t('download.showReqs') !== false && <Reveal delay={0.18}>
          <div className="h-full rounded-[2rem] border border-brand-line bg-white p-8" data-testid="download-reqs">
            <h3 className="font-display text-lg font-extrabold text-brand-ink">{t('download.reqs.title')}</h3>
            <ul className="mt-5 divide-y divide-brand-line">
              {reqs.map((r) => (
                <li key={r.k} className="flex items-center justify-between py-3" data-testid={`req-row-${r.k}`}>
                  <span className="text-sm font-bold text-brand-ink">{r.k}</span>
                  <span className="font-mono text-xs text-brand-steel" dir="ltr">{r.v}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>}
      </section>
    </div>
  );
}
